import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { IAction, SlicePropsWithData } from '../store';
import { urlSessionCheck, urlSessionUpdate } from '../../Constants';

const initialState: ISessionState = {
    isExpired: false,
    lastRequest: Date.now(),
    data: null,
    loading: false,
    error: null,
};

const thunks = {
    getSessionState: async (): Promise<ISessionCheckResult> => {
        try {
            const response = await fetch(urlSessionCheck);

            return response.ok
                ? Promise.resolve((await response.json()) as ISessionCheckResult)
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },
    updateSessionState: async (): Promise<ISessionCheckResult> => {
        try {
            const request: RequestInit = {
                method: 'POST',
            };
            const response = await fetch(urlSessionUpdate, request);

            return response.ok
                ? ((await response.json()) as ISessionCheckResult)
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    _reducers: (builder: ActionReducerMapBuilder<ISessionState>) => {
        builder.addCase(getSessionState.pending, (state: Draft<ISessionState>) => {
            state.lastRequest = Date.now();
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            getSessionState.fulfilled,
            (state: Draft<ISessionState>, action: IAction<ISessionCheckResult>) => {
                state.isExpired = false;
                state.data = action.payload;
                state.loading = false;
                state.error = null;
            },
        );
        builder.addCase(
            getSessionState.rejected,
            (state: Draft<ISessionState>, action: IAction<SerializedError>) => {
                state.isExpired = true;
                state.loading = false;
                state.error = action.error;
            },
        );

        builder.addCase(updateSessionState.pending, (state: Draft<ISessionState>) => {
            state.lastRequest = Date.now();
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            updateSessionState.fulfilled,
            (state: Draft<ISessionState>, action: IAction<ISessionCheckResult>) => {
                state.isExpired = false;
                state.data = action.payload;
                state.loading = false;
                state.error = null;
            },
        );
        builder.addCase(
            updateSessionState.rejected,
            (state: Draft<ISessionState>, action: IAction<SerializedError>) => {
                state.isExpired = true;
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const getSessionState = createAsyncThunk(urlSessionCheck, thunks.getSessionState);
export const updateSessionState = createAsyncThunk(urlSessionUpdate, thunks.updateSessionState);

export const sessionSlice = createSlice({
    name: 'session',
    initialState,
    extraReducers: thunks._reducers,
    reducers: {},
});

interface ISessionCheckResult {
    /** Timestamp when the session will expire. */
    expirationTime: number;
}

export interface ISessionState extends SlicePropsWithData<ISessionCheckResult | null> {
    /** Wether the session is already expired. */
    isExpired: boolean;

    /** Timestamp when the last request was sent. */
    lastRequest: number;
}

export default sessionSlice.reducer;
